/**
 * Document Numbering Utilities
 *
 * Utility functions for document numbering series.
 */

import { getInvoiceTypeLabel } from './billing-utils';
import { ResetFrequency, NumberingSeries } from './numbering-types';

// ============================================================================
// Document Numbering Utilities
// ============================================================================

/**
 * Get reset frequency display label.
 */
export function getResetFrequencyLabel(frequency: ResetFrequency): string {
  const labels: Record<ResetFrequency, string> = {
    never: 'Never',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    yearly: 'Yearly',
  };
  return labels[frequency] || frequency;
}

/**
 * Get series type display label (Draft or Final).
 */
export function getSeriesTypeLabel(isDraft: boolean): string {
  return isDraft ? 'Draft (Proforma)' : 'Final';
}

/**
 * Format numbering series display name.
 * Example: "B2B Draft (PB-2526######)"
 */
export function formatSeriesDisplayName(series: NumberingSeries): string {
  const typeLabel = getInvoiceTypeLabel(series.documentType);
  const draftLabel = series.isDraft ? ' Draft' : '';
  const format = series.numberFormat;
  return `${typeLabel}${draftLabel} (${format})`;
}

/**
 * Preview number format with example values.
 * Example: "B-2526000001"
 */
export function previewNumberFormat(
  numberFormat: string,
  prefix: string,
  sequence: number = 1,
  fiscalYear: number = 2526
): string {
  let format = numberFormat;
  format = format.replace('{PREFIX}', prefix);
  format = format.replace('{YYYY}', fiscalYear.toString().padStart(4, '0'));
  format = format.replace('{YY}', (fiscalYear % 100).toString().padStart(2, '0'));

  const now = new Date();
  format = format.replace('{MM}', (now.getMonth() + 1).toString().padStart(2, '0'));

  // Count # for padding and replace with sequence number
  const hashMatch = format.match(/\{(#+)\}/);
  if (hashMatch?.[1]) {
    const seqPadding = hashMatch[1].length;
    const seqStr = sequence.toString().padStart(seqPadding, '0');
    format = format.replace(/\{#+\}/, seqStr);
  }

  return format;
}
